import type { StorageLike } from './attemptLog.ts';
import type { ChatReply } from './chatApi.ts';

/**
 * Dziennik rozmów z botem — tylko w tej przeglądarce (nigdzie nie jest wysyłany; serwer niczego z rozmowy nie zapisuje, SPEC §14.4).
 * Służy do strojenia: rodzic w trybie testowym ocenia odpowiedzi bota („dobra / zła”), a „Kopiuj rozmowy” daje tekst do wklejenia w rozmowie z Claude.
 */

export const CHAT_LOG_KEY = 'naative.chats.v1';
export const MAX_CHATS = 60;

export type TurnLabel = 'ok' | 'bad';

/** Skąd wypowiedź w zapisie lekcji (bot w zadaniach, SPEC §14.5): reakcja po próbie, pytanie dziecka, komentarz na koniec. */
export type TurnKind = 'react' | 'ask' | 'summary';

export interface ChatLogTurn {
  role: 'bot' | 'child';
  text: string;
  /** polskie wyjaśnienie bota */
  pl?: string;
  /** skąd odpowiedź bota (model / linijka awaryjna) */
  src?: ChatReply['source'];
  /** stała odpowiedź „powiedz dorosłemu” */
  safety?: true;
  /** ile razy pytaliśmy model */
  tries?: number;
  /** naruszenia zasad znalezione w odpowiedziach modelu */
  viol?: string[];
  /** pewność rozpoznania mowy dziecka */
  conf?: number;
  /** dziecko (rodzic) napisało zamiast powiedzieć */
  typed?: boolean;
  /** tylko w zapisie lekcji: rodzaj wypowiedzi i krótki opis sytuacji (zadanie, wynik próby) — do strojenia */
  k?: TurnKind;
  ctx?: string;
  label?: TurnLabel;
}

export interface ParentNote {
  /** ile razy dziecko odpowiedziało */
  turns: number;
  /** dzisiejsze słówka, które dziecko wypowiedziało */
  used: string[];
  /** ile dzisiejszych słówek było do użycia */
  of: number;
  /** ile razy bot tłumaczył po polsku */
  explained: number;
  /** ile odpowiedzi bota zastąpiła linijka z puli */
  fallbacks: number;
  /** ile razy bot odesłał dziecko do dorosłego (dziecko mówiło o krzywdzie, strachu, niebezpieczeństwie) */
  alerts?: number;
}

export interface ChatLogRecord {
  id: string;
  t: string;
  day: number;
  /** `lesson` = zapis wypowiedzi bota w zadaniach jednej lekcji (bez tego pola: zwykła rozmowa) */
  kind?: 'lesson';
  model?: string;
  turns: ChatLogTurn[];
  done: boolean;
  note?: ParentNote;
}

const isRecord = (x: unknown): x is ChatLogRecord => {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.t === 'string' && typeof o.day === 'number' && Array.isArray(o.turns);
};

export function loadChats(store: StorageLike | null): ChatLogRecord[] {
  if (!store) return [];
  try {
    const raw = store.getItem(CHAT_LOG_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecord) : [];
  } catch {
    return [];
  }
}

function save(store: StorageLike | null, list: ChatLogRecord[]): boolean {
  if (!store) return false;
  try {
    store.setItem(CHAT_LOG_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

/** Zapisuje zmianę jednej rozmowy; brak rozmowy = nic nie robi. */
function update(store: StorageLike | null, id: string, change: (r: ChatLogRecord) => void): boolean {
  const list = loadChats(store);
  const rec = list.find((r) => r.id === id);
  if (!rec) return false;
  change(rec);
  return save(store, list);
}

/** Czy w zapisie jest coś do pokazania: odpowiedź dziecka albo wypowiedź bota w zadaniu. */
export const hasContent = (r: ChatLogRecord): boolean => r.turns.some((t) => t.role === 'child' || t.k !== undefined);

export function startChat(store: StorageLike | null, rec: Pick<ChatLogRecord, 'id' | 't' | 'day' | 'kind'>): boolean {
  const list = [...loadChats(store), { ...rec, turns: [], done: false }];
  while (list.length > MAX_CHATS) list.shift();
  return save(store, list);
}

export const appendTurn = (store: StorageLike | null, id: string, turn: ChatLogTurn): boolean => update(store, id, (r) => void r.turns.push(turn));

export function labelTurn(store: StorageLike | null, id: string, index: number, label: TurnLabel | undefined): boolean {
  return update(store, id, (r) => {
    const t = r.turns[index];
    if (!t) return;
    if (label === undefined) delete t.label;
    else t.label = label;
  });
}

export const finishChat = (store: StorageLike | null, id: string, note: ParentNote, model?: string): boolean =>
  update(store, id, (r) => {
    r.done = true;
    r.note = note;
    if (model) r.model = model;
  });

export function clearChats(store: StorageLike | null): void {
  try {
    store?.removeItem(CHAT_LOG_KEY);
  } catch {
    /* bez pamięci nie ma czego czyścić */
  }
}

export interface ChatStats {
  chats: number;
  turns: number;
  ok: number;
  bad: number;
}

export function chatStats(list: readonly ChatLogRecord[]): ChatStats {
  const turns = list.flatMap((r) => r.turns);
  return { chats: list.length, turns: turns.length, ok: turns.filter((t) => t.label === 'ok').length, bad: turns.filter((t) => t.label === 'bad').length };
}

/**
 * Tekst do skopiowania: nagłówek i po jednej linii JSON na rozmowę (najnowsze `limit`; puste rozmowy pomijamy).
 * Linia = jedna rozmowa, więc nawet ucięte wklejenie nie psuje wcześniejszych.
 */
export function exportChats(list: readonly ChatLogRecord[], limit = 10): string {
  const picked = list.filter(hasContent).slice(-limit);
  return [`naative-chats v1 · ${picked.length} rozmów`, ...picked.map((r) => JSON.stringify(r))].join('\n');
}

/** Dziennik dla aplikacji (null = przeglądarka bez dostępu do pamięci: nic się nie zapisuje, nic się nie psuje). */
export interface ChatStore {
  start(rec: Pick<ChatLogRecord, 'id' | 't' | 'day' | 'kind'>): void;
  add(id: string, turn: ChatLogTurn): void;
  label(id: string, index: number, label: TurnLabel | undefined): void;
  finish(id: string, note: ParentNote, model?: string): void;
  list(): ChatLogRecord[];
  clear(): void;
}

export function createChatStore(store: StorageLike | null): ChatStore {
  return {
    start: (rec) => void startChat(store, rec),
    add: (id, turn) => void appendTurn(store, id, turn),
    label: (id, index, label) => void labelTurn(store, id, index, label),
    finish: (id, note, model) => void finishChat(store, id, note, model),
    list: () => loadChats(store),
    clear: () => clearChats(store),
  };
}
